/* eslint-disable no-nested-ternary */
import React from 'react';
import {
  Box, Button, createStyles,
  Divider,
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import ChipSelect from '../ChipSelect';
import type {
  IRepairJob, IRepairItem, IFieldOptions, IAddRepairJob, IFields,
} from './EstimatorInterfaces';

const useStyles = makeStyles(() => createStyles({
  root: {
    flexGrow: 1,
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
  estimatorRadioButtons: {
    '& .MuiFormGroup-root, .MuiFormControlLabel-root, label': {
      marginBottom: '0',
    },
  },
}));

interface EstimatorUpsertRepairJobsProps {
  repairItems: IRepairItem[];
  handleAddRepairJob: (addRepairJobParam: IAddRepairJob) => void;
  editJob?: IAddRepairJob;
  barcode: string;
  loading?: boolean;
}

const EstimatorUpsertRepairJobs: React.FC<EstimatorUpsertRepairJobsProps> = (
  {
    repairItems, handleAddRepairJob, editJob, barcode, loading = false,
  },
) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedRepairItem, setSelectedRepairItem] = React.useState<Array<IRepairItem>>(editJob ? editJob.repairItems : repairItems);
  const [repairJobs, setRepairJobs] = React.useState<IRepairJob[]>([]);
  const [repairJob, setRepairJob] = React.useState<IRepairJob | null>(editJob?.selectedRepairJob || null);
  const [requiredJob, setRequiredJob] = React.useState(editJob?.required === false ? 'recommended' : 'required');
  const [selectedFieldOptions, setSelectedFieldOptions] = React.useState<Array<IFieldOptions | null>>(
    editJob?.selectedRepairJob?.fields
      ? editJob.selectedRepairJob.fields.map((f) => f.fieldOptions.find((fo) => fo?.selected === true))
      : new Array(repairJob?.fields.length),
  );

  React.useEffect(() => {
    axios.get<IRepairJob[]>('/estimator/getRepairJobs')
      .then((response) => {
        setRepairJobs(response.data);
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  }, [enqueueSnackbar]);

  const submitAddRepairJob = () => {
    if (repairJob) {
      const result : IAddRepairJob = {
        required: requiredJob === 'required',
        repairItems: selectedRepairItem,
        selectedRepairJob: repairJob,
      };
      result.selectedRepairJob.fields.forEach((field: IFields, idx: number) => {
        // eslint-disable-next-line no-param-reassign
        field.fieldOptions = [selectedFieldOptions[idx]];
      });

      handleAddRepairJob(result);
    } else {
      enqueueSnackbar('Please select values for the required fields.', { persist: false, variant: 'error' });
    }
  };

  const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredJob(event.target.value);
  };

  const handleSelectAllRepairItems = () => {
    if (selectedRepairItem.length === repairItems.length) {
      setSelectedRepairItem(new Array<IRepairItem>());
    } else {
      setSelectedRepairItem([...repairItems]);
    }
  };

  const handleSelectedRepairJobChange = (selectedRepairJob: IRepairJob | null) => {
    if (selectedRepairJob !== null) {
      axios.get<IRepairJob>('/estimator/getRepairJob', {
        params: {
          repairJobId: selectedRepairJob.id,
          barcode,
        },
      })
        .then((response) => {
          setRepairJob(response.data);
          setSelectedFieldOptions([]);
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
    } else {
      setRepairJob(null);
    }
  };

  const handleSelectRepairItem = (selectedRepairItemParam: IRepairItem) => {
    const updatedSelectedRepairItems = [...selectedRepairItem];
    const index = updatedSelectedRepairItems.indexOf(selectedRepairItemParam);

    if (index === -1) {
      updatedSelectedRepairItems.push(selectedRepairItemParam);
    } else {
      updatedSelectedRepairItems.splice(index, 1);
    }

    setSelectedRepairItem([...updatedSelectedRepairItems]);
  };

  const handleFieldOptionsChange = (idx: number, selectedOption: IFieldOptions | null) => {
    if (selectedOption) {
      const updatedFieldOptions = [...selectedFieldOptions];
      updatedFieldOptions[idx] = selectedOption;
      setSelectedFieldOptions(updatedFieldOptions);
    } else {
      const updatedFieldOptions = [...selectedFieldOptions];
      updatedFieldOptions[idx] = null;
      setSelectedFieldOptions(updatedFieldOptions);
    }
  };

  const filterOptions = (options: (IFieldOptions | null)[], inputValue: string) => {
    const startsWithMatches = options.filter((option) => option?.name.toLowerCase().startsWith(inputValue));

    const includesMatches = options.filter((option) => option?.name.toLowerCase().includes(inputValue) && !startsWithMatches.includes(option));

    return [...startsWithMatches, ...includesMatches];
  };

  return (
    <Box>
      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box pl={2}>
              <Typography variant="h5">{editJob ? 'Edit Repair Job' : 'Add Repair Job(s)'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <Box pl={2}>
              <Autocomplete
                value={repairJob}
                onChange={(event: unknown, selectedJob: IRepairJob | null) => {
                  handleSelectedRepairJobChange(selectedJob);
                }}
                autoHighlight
                autoSelect
                options={repairJobs}
                getOptionLabel={(option) => option.name.toString()}
                // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label="Search Repair Jobs" id="search-repair-jobs" variant="outlined" />}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box pr={2}>
              <RadioGroup aria-label="required" name="required" value={requiredJob} onChange={handleRequiredChange}>
                <FormControlLabel
                  value="required"
                  label="Required Job"
                  className={classes.estimatorRadioButtons}
                  control={<Radio color="primary" size="small" id="required-job-radio-button" />}
                />
                <FormControlLabel
                  value="recommended"
                  label="Recommended Job"
                  control={<Radio color="primary" size="small" id="recommended-job-radio-button" />}
                />
              </RadioGroup>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box pl={2} my={2}>
              <Typography variant="h6">{'Apply Repair Job to Repair Item(s)'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} container spacing={0}>
            <Grid item>
              <Box pl={1.5}>
                <ChipSelect
                  label="Select All"
                  selected={selectedRepairItem.length === repairItems.length}
                  handleClick={() => handleSelectAllRepairItems()}
                />
              </Box>
            </Grid>
            {repairItems.length > 1 && repairItems.map((repairItem) => (
              <Grid item>
                <Box pl={1.5}>
                  <ChipSelect
                    key={repairItem.id}
                    label={repairItem.repairItemLabel}
                    selected={!!selectedRepairItem.find((e) => e.id === repairItem.id)}
                    handleClick={() => handleSelectRepairItem(repairItem)}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          {repairJob && repairJob.fields.length > 0 && (
            <Grid item xs={12}>
              <Box pl={2} mt={2} mb={2}>
                <Typography variant="h6">{`${repairJob.name} Data`}</Typography>
              </Box>
              <Grid container justifyContent="flex-start">
                {repairJob.fields.map((field, i) => (
                  <Grid item xs={12} md={6}>
                    <Box pl={2} m={1}>
                      <Autocomplete
                        key={field.id}
                        value={selectedFieldOptions[i]}
                        onChange={(event: unknown, selectedOption: IFieldOptions | null) => {
                          handleFieldOptionsChange(i, selectedOption);
                        }}
                        autoHighlight
                        autoSelect
                        options={field?.fieldOptions}
                        getOptionLabel={(option) => option?.name.toString() || ''}
                        filterOptions={(options, { inputValue }) => filterOptions(options, inputValue)}
                    // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                    // eslint-disable-next-line react/jsx-props-no-spreading
                        renderInput={(params) => <TextField {...params} label={`Select ${field.name}`} variant="outlined" />}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
          <Grid container item xs={12} justifyContent="flex-end">
            <Box m={2}>
              <Button
                style={{ width: editJob ? '15em' : '10em' }}
                disabled={((loading)
                  || (repairJob?.fields?.length
                    ? selectedFieldOptions.filter((option) => (option?.selected || option?.id)).length < (repairJob?.fields?.length || 1)
                    : Boolean(!repairJob)))}
                variant="contained"
                color="primary"
                onClick={submitAddRepairJob}
              >
                {loading ? <CircularProgress size={25} />
                  : editJob ? 'Update Repair Job' : 'Add Repair Job'}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Paper>

    </Box>
  );
};

export default EstimatorUpsertRepairJobs;
